import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, Menu, X, Home, MessageCircle, ChevronDown } from 'lucide-react';
import { PHONE, PHONE_LINK, WHATSAPP, ADDRESS, COMPANY, SERVICE_PAGES } from '../constants';

const REGION_PAGES = [
  { title: 'Räumung Niederösterreich', slug: '/raeumung-niederoesterreich' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [leistungenOpen, setLeistungenOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavClick = (sectionId) => {
    setMobileMenuOpen(false);
    if (isHome) {
      const el = document.getElementById(sectionId);
      el?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        el?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  };

  const handleKontakt = () => {
    setMobileMenuOpen(false);
    const el = document.getElementById('kontakt');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else if (!isHome) {
      navigate('/');
      setTimeout(() => {
        document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  };

  const navItems = [
    { label: 'Leistungen', id: 'leistungen' },
    { label: 'Preise', id: 'preise' },
    { label: 'Über Uns', id: 'ueber-uns' },
    { label: 'Bewertungen', id: 'bewertungen' },
    { label: 'Kontakt', id: 'kontakt' },
  ];

  return (
    <>
      {/* Pre-header */}
      <div className="bg-[#1A1A2E] text-white text-xs py-2 hidden sm:block" data-testid="pre-header">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-6 flex-wrap">
            <a href={PHONE_LINK} className="flex items-center gap-1.5 hover:text-[#E8722A] transition-colors font-medium">
              <Phone size={12} /> {PHONE}
            </a>
            <span className="text-[#FAFAF8]/50">Mo–Sa: 07:00–20:00 Uhr</span>
          </div>
          <a href={WHATSAPP} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 bg-[#25D366] text-white px-3 py-1 rounded font-bold hover:bg-[#20bd5a] transition-colors">
            <MessageCircle size={12} /> WhatsApp
          </a>
        </div>
      </div>

      {/* Main header */}
      <header
        data-testid="sticky-header"
        className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#FAFAF8] shadow-xl' : 'bg-[#FAFAF8] shadow-sm border-b border-gray-200/60'}`}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo">
              <img
                src="/wappen-wien.svg"
                alt="Wiener Wappen"
                className="h-10 w-auto"
              />
              <div className="leading-tight">
                <div className="cabinet font-black text-[#1A1A2E] text-sm sm:text-base tracking-tight">Wohnungsräumung</div>
                <div className="font-bold text-[#E8722A] text-xs">Wien</div>
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-7">
              {/* Leistungen Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setLeistungenOpen(true)}
                onMouseLeave={() => setLeistungenOpen(false)}
              >
                <button
                  className="flex items-center gap-1 text-[#1A1A2E]/70 hover:text-[#E8722A] font-semibold text-sm transition-colors"
                  onClick={() => handleNavClick('leistungen')}
                >
                  Leistungen
                  <ChevronDown size={14} className={`transition-transform duration-200 ${leistungenOpen ? 'rotate-180 text-[#E8722A]' : ''}`} />
                </button>
                {leistungenOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-64 z-50">
                    <div className="bg-white rounded-xl shadow-2xl border border-gray-100 py-2 overflow-hidden">
                      <p className="px-4 pt-1 pb-2 text-[#E8722A] font-black text-xs uppercase tracking-wider border-b border-gray-100">
                        Leistungen Wien
                      </p>
                      {SERVICE_PAGES.map((s) => (
                        <Link
                          key={s.slug}
                          to={s.slug}
                          onClick={() => setLeistungenOpen(false)}
                          className="block px-4 py-2.5 text-sm text-[#1A1A2E] hover:bg-[#FAFAF8] hover:text-[#E8722A] font-medium transition-colors"
                        >
                          {s.title}
                        </Link>
                      ))}
                      <div className="border-t border-gray-100 mt-1 pt-1">
                        {REGION_PAGES.map((r) => (
                          <Link
                            key={r.slug}
                            to={r.slug}
                            onClick={() => setLeistungenOpen(false)}
                            className="block px-4 py-2.5 text-sm text-[#1A1A2E] hover:bg-[#FAFAF8] hover:text-[#E8722A] font-medium transition-colors"
                          >
                            {r.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* Other nav items */}
              {navItems.filter((i) => i.id !== 'leistungen').map((item) => (
                <button key={item.label} onClick={() => item.id === 'kontakt' ? handleKontakt() : handleNavClick(item.id)}
                  className="text-[#1A1A2E]/70 hover:text-[#E8722A] font-semibold text-sm transition-colors">
                  {item.label}
                </button>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-4">
              <a href={PHONE_LINK} className="flex items-center gap-2 text-[#1A1A2E] font-bold text-sm hover:text-[#E8722A] transition-colors">
                <Phone size={15} /> {PHONE}
              </a>
              <button onClick={handleKontakt} data-testid="header-cta-btn"
                className="bg-[#E8722A] hover:bg-[#d4651e] text-white font-black px-5 py-2.5 rounded-lg transition-colors text-sm">
                Kostenlos anfragen
              </button>
            </div>

            <div className="flex lg:hidden items-center gap-3">
              <a href={PHONE_LINK} className="text-[#1A1A2E] p-1"><Phone size={20} /></a>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-[#1A1A2E] p-1">
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#FAFAF8] border-t border-gray-200/60 shadow-xl">
            <div className="px-4 py-4 flex flex-col gap-1">
              {/* Leistungen direkt anzeigen */}
              <p className="text-[#E8722A] font-black text-xs uppercase tracking-wider px-2 pt-1 pb-0.5">Leistungen</p>
              {SERVICE_PAGES.map((s) => (
                <Link
                  key={s.slug}
                  to={s.slug}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-left text-[#1A1A2E]/80 hover:text-[#E8722A] hover:bg-gray-100 font-semibold py-2.5 px-3 rounded-lg text-sm transition-colors"
                >
                  {s.title}
                </Link>
              ))}
              {REGION_PAGES.map((r) => (
                <Link
                  key={r.slug}
                  to={r.slug}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-left text-[#1A1A2E]/80 hover:text-[#E8722A] hover:bg-gray-100 font-semibold py-2.5 px-3 rounded-lg text-sm transition-colors"
                >
                  {r.title}
                </Link>
              ))}
              <div className="border-t border-gray-200 my-1" />
              {/* Other nav items */}
              {navItems.filter((i) => i.id !== 'leistungen').map((item) => (
                <button key={item.label}
                  onClick={() => item.id === 'kontakt' ? handleKontakt() : handleNavClick(item.id)}
                  className="text-left text-[#1A1A2E] hover:text-[#E8722A] hover:bg-gray-100 font-semibold py-3 px-2 rounded-lg transition-colors">
                  {item.label}
                </button>
              ))}
              <button onClick={handleKontakt}
                className="bg-[#E8722A] text-white font-black px-4 py-3.5 rounded-lg mt-2">
                Kostenlos anfragen
              </button>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
